/**
 * Minimal, lifecycle-safe mount for Gnosis Pay's V1 PSE card-data frame.
 *
 * @gnosispay/pse-sdk@2.0.1 appends the iframe before assigning `onload` and
 * sends its init message only once. A missed load/listener timing window leaves
 * the PSE app in its intentional empty pre-init state. This mount preserves the
 * SDK's documented message shape while registering every listener before the
 * iframe can navigate or enter the document.
 */

const PSE_SDK_VERSION = "2.0.1";
const LOAD_TIMEOUT_MS = 12_000;
const INIT_SETTLE_MS = 150;

export interface PseElementHandle {
  destroy(): void;
}

export interface PseCardDataConfig {
  appId: string;
  iframeHost: string;
  cardToken: string;
  ephemeralToken: string;
  gnosisPayApiAuthToken: string;
  onInvalidToken?: (message?: string) => void;
  onError?: (message: string) => void;
}

interface PseFrameMessage {
  type?: string;
  message?: unknown;
}

/**
 * Mount and initialise the secure frame. Resolves only after `init` has been
 * posted, so callers can distinguish network/frame-load work from rendering.
 */
export function mountPseCardData(
  selector: string,
  config: PseCardDataConfig,
): Promise<PseElementHandle> {
  const container = document.querySelector<HTMLElement>(selector);
  if (!container) return Promise.reject(new Error(`Card display container '${selector}' was not found.`));

  const iframe = document.createElement("iframe");
  iframe.title = "Secure card details";
  iframe.allow = "clipboard-write";
  iframe.referrerPolicy = "no-referrer";
  iframe.style.width = "100%";
  iframe.style.height = "100%";
  iframe.style.display = "block";
  iframe.style.border = "0";
  iframe.style.background = "#ffffff";

  let destroyed = false;
  let initTimer: ReturnType<typeof setTimeout> | undefined;
  let loadTimer: ReturnType<typeof setTimeout> | undefined;

  const onMessage = (event: MessageEvent<unknown>) => {
    if (event.origin !== config.iframeHost || event.source !== iframe.contentWindow) return;
    if (!event.data || typeof event.data !== "object") return;
    const message = event.data as PseFrameMessage;
    const text = typeof message.message === "string" ? message.message : undefined;
    if (message.type === "EphemeralTokenInvalid") {
      config.onInvalidToken?.(text);
    } else if (message.type === "Error") {
      config.onError?.(text ?? "Card display error");
    }
  };

  window.addEventListener("message", onMessage);

  const handle: PseElementHandle = {
    destroy() {
      if (destroyed) return;
      destroyed = true;
      if (initTimer) clearTimeout(initTimer);
      if (loadTimer) clearTimeout(loadTimer);
      window.removeEventListener("message", onMessage);
      iframe.onload = null;
      iframe.onerror = null;
      iframe.remove();
    },
  };

  return new Promise<PseElementHandle>((resolve, reject) => {
    const fail = (error: Error) => {
      handle.destroy();
      reject(error);
    };

    // Register load/error handlers BEFORE assigning src or inserting the
    // iframe. The short post-load delay lets the child React app commit its
    // message listener before this one-shot init message is delivered.
    iframe.onload = () => {
      if (loadTimer) clearTimeout(loadTimer);
      initTimer = setTimeout(() => {
        if (destroyed) return;
        if (!iframe.contentWindow) {
          fail(new Error("Secure card frame is not accessible."));
          return;
        }
        iframe.contentWindow.postMessage(
          {
            type: "init",
            cardToken: config.cardToken,
            ephemeralToken: config.ephemeralToken,
            gnosisPayApiAuthToken: config.gnosisPayApiAuthToken,
            elementType: "cardData",
            appId: config.appId,
            version: PSE_SDK_VERSION,
            style: {
              container: { backgroundColor: "#ffffff", color: "#111111" },
              label: { color: "#6e6c62" },
              value: { color: "#111111" },
            },
          },
          config.iframeHost,
        );
        resolve(handle);
      }, INIT_SETTLE_MS);
    };
    iframe.onerror = () => fail(new Error("Secure card frame failed to load."));
    loadTimer = setTimeout(
      () => fail(new Error("Secure card frame did not load. Check your connection and try again.")),
      LOAD_TIMEOUT_MS,
    );

    iframe.src = `${config.iframeHost}/?appId=${encodeURIComponent(config.appId)}`;
    container.innerHTML = "";
    container.appendChild(iframe);
  });
}
